import React from 'react';
import { useIntl } from 'react-intl';
import { useShouldShowDrawer } from 'signer-app/utils/use-should-show-drawer';
import { WhiteLabelThemeProvider } from 'signer-app/white-labeling/theme-provider';
import {
  ThemeProviderProps,
  Box,
  Stack,
  Split,
} from '@dropbox/dig-foundations';
import { Text } from '@dropbox/dig-components/typography';
import { EsignDisclosureControl } from 'signer-app/esign-disclosure/esign-disclosure-control';
import { NextButton } from 'signer-app/signer-experience/next-button';
import messages from 'signer-app/signer-experience/signer-toolbar/messages';
import styles from 'signer-app/signer-experience/signer-toolbar/signer-toolbar.module.css';
import { SignWordmark, WordmarkLogo } from '@dropbox/dig-logos';
import poweredByLogo from 'signer-app/white-labeling/powered-by-dropbox-sign.svg';
import poweredByLogoColor from 'signer-app/white-labeling/powered-by-dropbox-sign-color.svg';
import { useSignerContext } from '../signer-experience';
import GetStartedBanner from '../action-banners/get-started-banner';

type ColorMode = ThemeProviderProps['mode'];

interface Props {
  customLogoUri?: string;
  colorMode: ColorMode;
  toolbarMenu: React.ReactNode;
  requiredFieldsLeft: number;
  requiredAttachmentsLeft: number | undefined;
  hasCustomStyles: boolean;
  mobileToggle: React.ReactNode;
  showEsignDisclosure: boolean;
  showNextButton: boolean;
}

export function PreviewStepToolbar({
  customLogoUri,
  colorMode,
  toolbarMenu,
  requiredFieldsLeft,
  requiredAttachmentsLeft,
  hasCustomStyles,
  mobileToggle,
  showEsignDisclosure,
  showNextButton,
}: Props) {
  const showPoweredByLogo = customLogoUri && !hasCustomStyles;
  const intl = useIntl();
  const shouldUseDrawer = useShouldShowDrawer();
  const { hideFormViewToggle, isMobileReady } = useSignerContext();
  const isFormViewAvailable = !hideFormViewToggle && isMobileReady;

  return (
    <WhiteLabelThemeProvider mode={colorMode}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding="Micro Small"
        backgroundColor="Secondary Base"
      >
        {customLogoUri ? (
          <Box padding="Micro Small" display="flex">
            <img
              src={customLogoUri}
              alt={intl.formatMessage(messages.brandLogo)}
              className={
                shouldUseDrawer ? styles.customLogoInDrawer : styles.customLogo
              }
            />
          </Box>
        ) : (
          <SignLogo mode={'dark'} />
        )}
        {!shouldUseDrawer && !isFormViewAvailable && mobileToggle}
      </Box>
      {!isFormViewAvailable && showEsignDisclosure ? (
        <>
          <PreviewToolbarSeparator colorMode={'dark'} />
          <Box
            paddingX="Micro XLarge"
            paddingY="Micro Large"
            backgroundColor="Background Subtle"
            display="flex"
          >
            <EsignDisclosureControl />
          </Box>
        </>
      ) : null}

      <PreviewToolbarSeparator colorMode={colorMode} />
      {isFormViewAvailable ? (
        <GetStartedBanner
          toolbarMenu={toolbarMenu}
          esignDisclosure={showEsignDisclosure}
        />
      ) : (
        <Box
          paddingLeft="Micro XLarge"
          paddingY={'Micro Small'}
          paddingRight="Micro Small"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          backgroundColor="Background Subtle"
        >
          <Stack gap="Micro Small">
            <Text
              variant="paragraph"
              size="medium"
              color={hasCustomStyles ? 'subtle' : 'standard'}
            >
              {requiredAttachmentsLeft
                ? intl.formatMessage(messages.requiredFieldsAndAttachments, {
                    requiredFieldsLeft,
                    requiredAttachmentsLeft,
                  })
                : intl.formatMessage(messages.requiredFields, {
                    requiredFields: requiredFieldsLeft,
                  })}
            </Text>
          </Stack>
          <Split gap="Micro Small" alignY="center">
            {showPoweredByLogo ? (
              <Split.Item>
                <Box display="flex" paddingX="Micro Small">
                  <img
                    src={
                      colorMode === 'bright'
                        ? poweredByLogoColor
                        : poweredByLogo
                    }
                    className={styles.poweredByLogo}
                    alt={intl.formatMessage(messages.poweredByDropbox)}
                  />
                </Box>
              </Split.Item>
            ) : null}
            {showNextButton && (
              <Split.Item>
                <NextButton />
              </Split.Item>
            )}
            <Split.Item>{toolbarMenu}</Split.Item>
          </Split>
        </Box>
      )}
    </WhiteLabelThemeProvider>
  );
}

function SignLogo({ mode }: { mode: ColorMode }) {
  return (
    // When rendering the logo, reset any override colors so they don't
    // bleed through. We just want to display either the base light or dark
    // logo
    <WhiteLabelThemeProvider colors="reset" mode={mode}>
      <Box color="Text Base" display="flex">
        <WordmarkLogo size={32} src={SignWordmark} />
      </Box>
    </WhiteLabelThemeProvider>
  );
}

function PreviewToolbarSeparator({ colorMode }: { colorMode: ColorMode }) {
  /**
   * The separator styles are always using the "bright" theme
   * because they are independent of the custom white-labele theme
   */
  return (
    <WhiteLabelThemeProvider mode="bright" colors="reset">
      <Box
        className={styles.toolbarSeparator}
        backgroundColor={
          colorMode === 'dark' ? 'Secondary Base - State 1' : 'Border Subtle'
        }
      />
    </WhiteLabelThemeProvider>
  );
}
